<template>
  <v-container fluid class="pa-4">
    <v-row class="mb-6">
      <v-col cols="12">
        <h1 class="text-h4 font-weight-bold primary--text">
          Generar Nuevo Convenio de Golf
        </h1>
        <p class="text-subtitle2 grey--text mt-2">
          Crea un nuevo convenio y asígnalo a un socio
        </p>
      </v-col>
    </v-row>

    <!-- Stepper -->
    <v-stepper v-model="step" class="elevation-2">
      <!-- Step 1: Seleccionar Campo -->
      <v-stepper-header>
        <v-stepper-step :complete="step > 1" step="1">
          Seleccionar Campo
        </v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step :complete="step > 2" step="2">
          Tipo de Convenio
        </v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step :complete="step > 3" step="3">
          Asignar Socio
        </v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step step="4">
          Vista Previa
        </v-stepper-step>
      </v-stepper-header>

      <v-stepper-items>
        <!-- Step 1 -->
        <v-stepper-content step="1">
          <v-card class="mb-4">
            <v-card-title>Selecciona un Campo</v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <v-row>
                <v-col
                  v-for="campo in campos"
                  :key="campo.id"
                  cols="12"
                  sm="6"
                  md="4"
                >
                  <v-card
                    :class="{ 'selected-card': convenio.campo === campo.id }"
                    class="cursor-pointer"
                    @click="convenio.campo = campo.id"
                  >
                    <v-card-text class="pa-4">
                      <div class="text-h6 font-weight-bold">{{ campo.name || campo.nombre }}</div>
                      <div class="text-caption mt-2">{{ campo.location || campo.ubicacion }}</div>
                      <div class="text-caption grey--text mt-1">Par: {{ campo.par }}</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-btn color="primary" @click="step = 2" :disabled="!convenio.campo">
            Continuar
          </v-btn>
        </v-stepper-content>

        <!-- Step 2 -->
        <v-stepper-content step="2">
          <v-card class="mb-4">
            <v-card-title>Selecciona el Tipo de Convenio</v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <v-row>
                <v-col
                  v-for="tipo in tiposConvenios"
                  :key="tipo.id"
                  cols="12"
                  sm="6"
                  md="4"
                >
                  <v-card
                    :class="{ 'selected-card': convenio.tipo === tipo.id }"
                    class="cursor-pointer"
                    @click="convenio.tipo = tipo.id"
                  >
                    <v-card-text class="pa-4">
                      <div class="text-h6 font-weight-bold">{{ tipo.nombre }}</div>
                      <div class="text-caption mt-2">{{ tipo.descripcion }}</div>
                      <div class="text-caption mt-2 primary--text font-weight-bold">
                        Descuento: {{ tipo.descuento }}%
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-btn text @click="step = 1">
            Atrás
          </v-btn>
          <v-btn color="primary" @click="step = 3" :disabled="!convenio.tipo">
            Continuar
          </v-btn>
        </v-stepper-content>

        <!-- Step 3 -->
        <v-stepper-content step="3">
          <v-card class="mb-4">
            <v-card-title>Asignar a un Socio</v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <!-- Búsqueda Única de Usuario -->
              <v-row class="mb-4">
                <v-col cols="12">
                  <v-autocomplete
                    v-model="convenio.socio"
                    :items="usuariosDisponibles"
                    item-text="full_name"
                    item-value="id"
                    :loading="buscandoUsuarios"
                    :search-input.sync="busquedaUsuario"
                    label="Buscar socio..."
                    outlined
                    dense
                    clearable
                    :no-data-text="buscandoUsuarios ? 'Buscando...' : 'No hay resultados'"
                    @input="onSocioSeleccionado"
                  >
                    <template v-slot:item="{ item }">
                      <div class="d-flex align-center w-100">
                        <v-avatar size="32" class="mr-3" color="primary" text-color="white">
                          <span class="text-caption">
                            {{ (item.first_name?.[0] || item.primer_nombre?.[0] || '') +
                               (item.last_name?.[0] || item.primer_apellido?.[0] || '') }}
                          </span>
                        </v-avatar>
                        <div class="flex-grow-1">
                          <div class="font-weight-bold">{{ item.full_name }}</div>
                          <div class="text-caption grey--text">
                            {{ item.email ? item.email : (item.socio ? 'Socio: ' + item.socio : 'ID: ' + item.id) }}
                          </div>
                        </div>
                      </div>
                    </template>
                    <template v-slot:selection="{ item }">
                      <div class="d-flex align-center">
                        <v-avatar size="24" class="mr-2" color="primary" text-color="white">
                          <span class="text-caption">
                            {{ (item.first_name?.[0] || item.primer_nombre?.[0] || '') +
                               (item.last_name?.[0] || item.primer_apellido?.[0] || '') }}
                          </span>
                        </v-avatar>
                        <span>{{ item.full_name }}</span>
                      </div>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>

              <!-- Información del Socio Seleccionado -->
              <div v-if="socioSeleccionado" class="pa-4 bg-light-blue rounded">
                <div class="font-weight-bold mb-3">Información del Socio Seleccionado:</div>
                <div class="text-caption">
                  <div><strong>Nombre:</strong> {{ socioSeleccionado.full_name }}</div>
                  <div v-if="socioSeleccionado.email"><strong>Email:</strong> {{ socioSeleccionado.email }}</div>
                  <div v-if="socioSeleccionado.socio"><strong>Socio #:</strong> {{ socioSeleccionado.socio }}</div>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <v-btn text @click="step = 2">
            Atrás
          </v-btn>
          <v-btn
            color="primary"
            @click="step = 4"
            :disabled="!convenio.socio"
          >
            Continuar
          </v-btn>
        </v-stepper-content>

        <!-- Step 4 -->
        <v-stepper-content step="4">
          <v-card class="mb-4">
            <v-card-title>Vista Previa del Convenio</v-card-title>
            <v-divider></v-divider>
            <v-card-text>
              <div class="convenio-template pa-6 bg-grey-lighten-5 rounded">
                <div class="text-center mb-6">
                  <h2 class="text-h5 font-weight-bold primary--text">CONVENIO DE GOLF</h2>
                  <div class="text-caption grey--text">{{ new Date().toLocaleDateString('es-ES') }}</div>
                </div>

                <div class="mb-6">
                  <div class="font-weight-bold mb-2">CAMPO:</div>
                  <div class="pl-4">
                    <div>{{ campoSeleccionado?.name || campoSeleccionado?.nombre }}</div>
                    <div class="text-caption grey--text">{{ campoSeleccionado?.location || campoSeleccionado?.ubicacion }}</div>
                  </div>
                </div>

                <div class="mb-6">
                  <div class="font-weight-bold mb-2">TIPO DE CONVENIO:</div>
                  <div class="pl-4">
                    <div>{{ tipoSeleccionado?.nombre }}</div>
                    <div class="text-caption grey--text">{{ tipoSeleccionado?.descripcion }}</div>
                    <div class="text-subtitle2 primary--text mt-2">Descuento: {{ tipoSeleccionado?.descuento }}%</div>
                  </div>
                </div>

                <div class="mb-6">
                  <div class="font-weight-bold mb-2">ASIGNADO A:</div>
                  <div class="pl-4">
                    <div class="d-flex align-center">
                      <v-avatar size="48" class="mr-3">
                        <span>{{ (socioSeleccionado?.first_name?.[0] || '') + (socioSeleccionado?.last_name?.[0] || '') }}</span>
                      </v-avatar>
                      <div>
                        <div class="font-weight-bold">{{ socioSeleccionado?.first_name }} {{ socioSeleccionado?.last_name }}</div>
                        <div class="text-caption grey--text">{{ socioSeleccionado?.email }}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <v-divider class="my-4"></v-divider>

                <div class="text-caption grey--text text-center">
                  Este convenio será enviado al correo, a través de la aplicación y se mostrará en el perfil del socio.
                </div>
              </div>
            </v-card-text>
          </v-card>

          <v-btn text @click="step = 3">
            Atrás
          </v-btn>
          <v-btn color="success" :loading="enviando" @click="generarConvenio">
            <v-icon left>mdi-check</v-icon>
            Generar y Enviar Convenio
          </v-btn>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" bottom right timeout="4000">
      {{ snackbar.mensaje }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'GolfConvenios',
  data () {
    return {
      step: 1,
      enviando: false,
      busquedaUsuario: '',
      buscandoUsuarios: false,
      snackbar: {
        show: false,
        mensaje: '',
        color: 'success'
      },
      convenio: {
        campo: null,
        tipo: null,
        socio: null
      },
      usuariosDisponibles: [],
      usuariosCCSLP: [],
      usuariosCCSLPBuscados: [],
      usuariosNormales: [],
      socios: []
    }
  },
  computed: {
    ...mapState('golfConvenios', {
      campos: 'campos',
      tiposConvenios: 'tipos',
      loadingTipos: 'loading'
    }),
    campoSeleccionado () {
      return this.campos.find(c => c.id === this.convenio.campo)
    },
    tipoSeleccionado () {
      return this.tiposConvenios.find(t => t.id === this.convenio.tipo)
    },
    socioSeleccionado () {
      return this.usuariosDisponibles.find(s => s.id === this.convenio.socio)
    }
  },
  watch: {
    busquedaUsuario (val) {
      if (!val || val.length < 2) {
        this.usuariosCCSLPBuscados = []
        this.actualizarUsuariosDisponibles()
        return
      }

      // Buscar en ambos tipos simultáneamente
      this.buscarUsuariosNormales(val)
      this.buscarUsuariosCCSLP(val)
    }
  },
  mounted () {
    this.cargarDatos()
  },
  methods: {
    ...mapActions('golfConvenios', ['fetchTipos', 'fetchCampos', 'createConvenio']),
    async cargarDatos () {
      try {
        await Promise.all([
          this.fetchTipos(),
          this.fetchCampos(),
          this.cargarUsuariosNormales(),
          this.cargarUsuariosCCSLP()
        ])
      } catch (error) {
        console.error('Error al cargar datos:', error)
        this.mostrarSnackbar('Error al cargar datos', 'error')
      }
    },
    async cargarUsuariosNormales () {
      try {
        const { golfConveniosAPI } = await import('@/services/golfConvenios')
        const response = await golfConveniosAPI.cargarResidents()

        this.usuariosNormales = (response.data.residents || []).map(resident => ({
          id: resident.idUser.id,
          full_name: resident.fullname,
          first_name: resident.idUser.first_name,
          last_name: resident.idUser.last_name,
          email: resident.idUser.email,
          cellNumber: resident.idUser.cellNumber,
          avatar: resident.idUser.avatar,
          address: resident.address,
          tipo: 'usuario'
        }))

        this.actualizarUsuariosDisponibles()
      } catch (error) {
        console.error('Error cargando usuarios normales:', error)
        this.usuariosNormales = []
      }
    },
    async cargarUsuariosCCSLP () {
      try {
        const { golfConveniosAPI } = await import('@/services/golfConvenios')
        const response = await golfConveniosAPI.cargarCCSLPUsers()

        this.usuariosCCSLP = (response.data.users || []).map(user => ({
          id: user.id,
          full_name: user.name,
          primer_nombre: user.name ? user.name.split(' ')[0] : '',
          primer_apellido: user.name ? user.name.split(' ').slice(1).join(' ') : '',
          socio: user.socio,
          id_dependiente: user.id_dependiente,
          status: user.status,
          activo: user.activo,
          is_moroso: user.is_moroso,
          tipo: 'ccslp',
          ccslp_user_id: user.id
        }))

        this.actualizarUsuariosDisponibles()
      } catch (error) {
        console.error('Error cargando CCSLP users:', error)
        this.usuariosCCSLP = []
      }
    },
    async buscarUsuariosCCSLP (termino) {
      try {
        this.buscandoUsuarios = true
        const { golfConveniosAPI } = await import('@/services/golfConvenios')
        const response = await golfConveniosAPI.buscarCCSLPUsers(termino)

        this.usuariosCCSLPBuscados = (response.data.users || []).map(user => ({
          id: user.id,
          full_name: user.name,
          primer_nombre: user.name ? user.name.split(' ')[0] : '',
          primer_apellido: user.name ? user.name.split(' ').slice(1).join(' ') : '',
          socio: user.socio,
          id_dependiente: user.id_dependiente,
          status: user.status,
          activo: user.activo,
          is_moroso: user.is_moroso,
          tipo: 'ccslp',
          ccslp_user_id: user.id
        }))

        this.actualizarUsuariosDisponibles()
      } catch (error) {
        console.error('Error buscando CCSLP users:', error)
        this.usuariosCCSLPBuscados = []
      } finally {
        this.buscandoUsuarios = false
      }
    },
    actualizarUsuariosDisponibles () {
      // Combinar usuarios normales y CCSLP
      const usuariosNormales = this.usuariosNormales || []
      const usuariosCCSLP = this.usuariosCCSLPBuscados.length > 0
        ? this.usuariosCCSLPBuscados
        : (this.usuariosCCSLP || []).slice(0, 100)

      this.usuariosDisponibles = [...usuariosNormales, ...usuariosCCSLP]
    },
    async generarConvenio () {
      this.enviando = true
      try {
        const socio = this.socioSeleccionado
        if (!socio) {
          this.mostrarSnackbar('Error: no se pudo identificar el socio', 'error')
          return
        }

        // Detectar tipo basándose en la propiedad 'tipo' del objeto del socio
        const esUsuarioNormal = socio.tipo === 'usuario'

        // Preparar datos según el tipo
        const datosConvenio = {
          campo_id: this.convenio.campo,
          tipo_id: this.convenio.tipo
        }

        if (esUsuarioNormal) {
          datosConvenio.user_id = this.convenio.socio
        } else {
          datosConvenio.ccslp_user_id = this.convenio.socio
        }

        // Crear convenio usando el método correcto del servicio
        const { golfConveniosAPI } = await import('@/services/golfConvenios')
        if (esUsuarioNormal) {
          await golfConveniosAPI.inscribirUserConvenio(datosConvenio)
        } else {
          await golfConveniosAPI.inscribirCCSLPConvenio(datosConvenio)
        }

        const nombreSocio = socio.full_name || socio.nombre

        this.mostrarSnackbar(
          `✓ Convenio generado y enviado a ${nombreSocio}`,
          'success'
        )

        // Reset form
        setTimeout(() => {
          this.step = 1
          this.convenio = {
            campo: null,
            tipo: null,
            socio: null
          }
          this.usuariosDisponibles = []
          this.busquedaUsuario = ''
        }, 2000)
      } catch (error) {
        console.error('Error:', error)
        this.mostrarSnackbar(error.response?.data?.message || 'Error al generar el convenio', 'error')
      } finally {
        this.enviando = false
      }
    },
    buscarUsuariosNormales (termino) {
      // Filtrar usuarios normales localmente
      if (!termino || termino.length < 2) {
        this.usuariosDisponibles = this.usuariosNormales
        return
      }

      this.usuariosDisponibles = this.usuariosNormales.filter(u =>
        (u.first_name + ' ' + u.last_name).toLowerCase().includes(termino.toLowerCase()) ||
        u.email?.toLowerCase().includes(termino.toLowerCase())
      )
    },
    onSocioSeleccionado () {
      console.log('Socio seleccionado:', this.convenio.socio)
      console.log('Datos del socio:', this.socioSeleccionado)
    },
    mostrarSnackbar (mensaje, color) {
      this.snackbar = {
        show: true,
        mensaje,
        color
      }
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
  transition: all 0.3s ease;
}

.cursor-pointer:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.selected-card {
  border: 3px solid #1976d2 !important;
  box-shadow: 0 4px 16px rgba(25, 118, 210, 0.3) !important;
}

.bg-light-blue {
  background-color: rgba(33, 150, 243, 0.1);
}

.bg-grey-lighten-5 {
  background-color: #f9f9f9;
}

.convenio-template {
  border: 2px solid #e0e0e0;
  background: white;
}
</style>
